import { MenuRenderer } from '@/components/menu-renderer';
import { ResponsiveImage } from '@/components/ResponsiveImage';
import { SocialMenuRenderer } from '@/components/social-menu-renderer';
import type { ResponsiveImageMedia } from '@/types/responsive-image';
import { Link } from '@inertiajs/react';
import { Mail, Phone } from 'lucide-react';
import { LRTLogo } from './LRTLogo';

// ── Static partner logo CDN constants ────────────────────────────────────────
// Uploaded via scripts/upload-public-static-to-cdn.php (2026-05-10).
// TLN-logo: source 1920×642 → variants at 480/768/1280/1920w in avif + webp.
const TLN_LOGO: ResponsiveImageMedia = {
    url: 'https://cdnweb.luxrallytravel.com/static-logos/2026/05/eba26f7a-5ba8-4c0d-a132-75906657d804.png',
    responsive_variants: {
        avif: {
            480: 'https://cdnweb.luxrallytravel.com/static-logos/2026/05/eba26f7a-5ba8-4c0d-a132-75906657d804-w480.avif',
            768: 'https://cdnweb.luxrallytravel.com/static-logos/2026/05/eba26f7a-5ba8-4c0d-a132-75906657d804-w768.avif',
            1280: 'https://cdnweb.luxrallytravel.com/static-logos/2026/05/eba26f7a-5ba8-4c0d-a132-75906657d804-w1280.avif',
            1920: 'https://cdnweb.luxrallytravel.com/static-logos/2026/05/eba26f7a-5ba8-4c0d-a132-75906657d804-w1920.avif',
        },
        webp: {
            480: 'https://cdnweb.luxrallytravel.com/static-logos/2026/05/eba26f7a-5ba8-4c0d-a132-75906657d804-w480.webp',
            768: 'https://cdnweb.luxrallytravel.com/static-logos/2026/05/eba26f7a-5ba8-4c0d-a132-75906657d804-w768.webp',
            1280: 'https://cdnweb.luxrallytravel.com/static-logos/2026/05/eba26f7a-5ba8-4c0d-a132-75906657d804-w1280.webp',
            1920: 'https://cdnweb.luxrallytravel.com/static-logos/2026/05/eba26f7a-5ba8-4c0d-a132-75906657d804-w1920.webp',
        },
    },
};


export default function PublicFooter() {
    const currentYear = new Date().getFullYear();

    return (
        <footer className="border-t border-[#F5F5F5]/10 bg-[#0b2222]">
            {/* ── Row 1: Main bar ─────────────────────────────────────── */}
            <div className="nav-px border-b border-[#F5F5F5]/10 py-10 lg:py-7">
                {/* ── Desktop xl+: single-row layout ─────────────────── */}
                <div className="hidden 2xl:flex 2xl:items-center 2xl:justify-between">
                    <LRTLogo className="h-8 w-auto flex-shrink-0" style={{ fill: '#F5F5F5' }} />

                    <div className="flex flex-1 items-center justify-end gap-5">
                        <img src="/EndorsedByLogo-FTG-REV.png" alt="Forbes Travel Guide Endorsed" width="422" height="192" className="h-9 w-auto object-contain opacity-50" />
                        <img src="/iata.svg" alt="IATA" width="355" height="217" className="h-7 w-auto object-contain opacity-50 brightness-0 invert" />
                        <ResponsiveImage
                            media={TLN_LOGO}
                            sizes="94px"
                            alt="Travel Leaders Network"
                            width={1920}
                            height={642}
                            className="h-7 w-auto object-contain opacity-50 brightness-0 invert"
                            loading="lazy"
                        />
                        <img src="/arc.png" alt="ARC" width="236" height="95" className="h-7 w-auto object-contain opacity-50 brightness-0 invert" />
                    </div>

                    <div className="mx-4 h-5 w-px bg-[#F5F5F5]/15" />

                    <div className="flex items-center gap-4">
                        <a
                            href="tel:+17868140750"
                            className="lux-body lux-body-sm flex items-center gap-2 text-[#F5F5F5]/60 transition-colors hover:text-[#F5F5F5]"
                        >
                            <Phone className="h-3.5 w-3.5 flex-shrink-0 text-[#F5F5F5]/40" />
                            +1 786 814 0750
                        </a>
                        <div className="h-4 w-px bg-[#F5F5F5]/15" />
                        <a
                            href="mailto:info@luxrallytravel.com"
                            className="lux-body lux-body-sm flex items-center gap-2 text-[#F5F5F5]/60 transition-colors hover:text-[#F5F5F5]"
                        >
                            <Mail className="h-3.5 w-3.5 flex-shrink-0 text-[#F5F5F5]/40" />
                            info@luxrallytravel.com
                        </a>
                        <div className="h-4 w-px bg-[#F5F5F5]/15" />
                        <SocialMenuRenderer
                            location="social_footer"
                            className="flex gap-4"
                            iconSize="h-4 w-4"
                            iconClassName="text-[#F5F5F5]/40 transition-colors hover:text-[#F5F5F5]"
                        />
                        <div className="h-4 w-px bg-[#F5F5F5]/15" />
                        <Link
                            href="/clients"
                            className="lux-caps lux-caps-sm inline-flex items-center justify-center rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] px-4 py-2.5 text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020] sm:py-1"
                        >
                            Become a Client
                        </Link>
                    </div>
                </div>

                {/* ── Tablet lg–2xl: single compact row ─────────────────── */}
                <div className="hidden lg:flex lg:items-center lg:justify-between 2xl:hidden">
                    {/* Left: Logo + endorsement logos */}
                    <div className="flex items-center gap-6">
                        <LRTLogo className="h-8 w-auto flex-shrink-0" style={{ fill: '#F5F5F5' }} />
                        <div className="h-5 w-px bg-[#F5F5F5]/15" />
                        <div className="flex items-center gap-5">
                            <img
                                src="/EndorsedByLogo-FTG-REV.png"
                                alt="Forbes Travel Guide Endorsed"
                                width="422"
                                height="192"
                                className="h-9 w-auto object-contain opacity-50"
                            />
                            <img src="/iata.svg" alt="IATA" width="355" height="217" className="h-7 w-auto object-contain opacity-50 brightness-0 invert" />
                            <ResponsiveImage
                                media={TLN_LOGO}
                                sizes="94px"
                                alt="Travel Leaders Network"
                                width={1920}
                                height={642}
                                className="h-7 w-auto object-contain opacity-50 brightness-0 invert"
                                loading="lazy"
                            />
                            <img src="/arc.png" alt="ARC" width="236" height="95" className="h-7 w-auto object-contain opacity-50 brightness-0 invert" />
                        </div>
                    </div>

                    {/* Right: Contact + social */}
                    <div className="flex items-center gap-4">
                        <a
                            href="tel:+17868140750"
                            className="lux-body lux-body-sm flex items-center gap-2 text-[#F5F5F5]/60 transition-colors hover:text-[#F5F5F5]"
                        >
                            <Phone className="h-3.5 w-3.5 flex-shrink-0 text-[#F5F5F5]/40" />
                            +1 786 814 0750
                        </a>
                        <div className="h-4 w-px bg-[#F5F5F5]/15" />
                        <a
                            href="mailto:info@luxrallytravel.com"
                            className="lux-body lux-body-sm flex items-center gap-2 text-[#F5F5F5]/60 transition-colors hover:text-[#F5F5F5]"
                        >
                            <Mail className="h-3.5 w-3.5 flex-shrink-0 text-[#F5F5F5]/40" />
                            info@luxrallytravel.com
                        </a>
                        <div className="h-4 w-px bg-[#F5F5F5]/15" />
                        <SocialMenuRenderer
                            location="social_footer"
                            className="flex gap-4"
                            iconSize="h-4 w-4"
                            iconClassName="text-[#F5F5F5]/40 transition-colors hover:text-[#F5F5F5]"
                        />
                    </div>
                </div>

                {/* ── Mobile: stacked layout ──────────────────────────── */}
                <div className="flex flex-col items-center lg:hidden">
                    {/* Brand */}
                    <LRTLogo className="h-8 w-auto flex-shrink-0" style={{ fill: '#F5F5F5' }} />

                    {/* Endorsements (tight to logo as a credentials pair) */}
                    <div className="mt-6 flex items-center gap-5">
                        <img
                            src="/EndorsedByLogo-FTG-REV.png"
                            alt="Forbes Travel Guide Endorsed"
                            width="422"
                            height="192"
                            className="h-4.5 w-auto object-contain opacity-50"
                        />
                        <img src="/iata.svg" alt="IATA" width="355" height="217" className="h-4.5 w-auto object-contain opacity-50 brightness-0 invert" />
                        <ResponsiveImage
                            media={TLN_LOGO}
                            sizes="94px"
                            alt="Travel Leaders Network"
                            width={1920}
                            height={642}
                            className="h-4.5 w-auto object-contain opacity-50 brightness-0 invert"
                            loading="lazy"
                        />
                        <img src="/arc.png" alt="ARC" width="236" height="95" className="h-4.5 w-auto object-contain opacity-50 brightness-0 invert" />
                    </div>

                    {/* Contact group: phone + email + social all together */}
                    <div className="mt-10 flex flex-col items-center gap-3">
                        <a
                            href="tel:+17868140750"
                            className="lux-body lux-body-sm flex items-center gap-2.5 text-[#F5F5F5]/60 transition-colors hover:text-[#F5F5F5]"
                        >
                            <Phone className="h-4 w-4 flex-shrink-0 text-[#F5F5F5]/40" />
                            +1 786 814 0750
                        </a>
                        <a
                            href="mailto:info@luxrallytravel.com"
                            className="lux-body lux-body-sm flex items-center gap-2.5 text-[#F5F5F5]/60 transition-colors hover:text-[#F5F5F5]"
                        >
                            <Mail className="h-4 w-4 flex-shrink-0 text-[#F5F5F5]/40" />
                            info@luxrallytravel.com
                        </a>
                        <SocialMenuRenderer
                            location="social_footer"
                            className="mt-2 flex gap-5"
                            iconSize="h-5 w-5"
                            iconClassName="text-[#F5F5F5]/40 transition-colors hover:text-[#F5F5F5]"
                        />
                    </div>

                    {/* CTA — sub-2xl single button (caption removed). */}
                    <Link
                        href="/clients"
                        className="lux-caps lux-caps-sm mt-10 inline-flex items-center justify-center rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] px-4 py-2.5 text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020] sm:py-1"
                    >
                        Become a Client
                    </Link>
                </div>
            </div>

            {/* ── Row 2: Bottom bar ───────────────────────────────────── */}
            <div className="nav-px py-5">
                <div className="flex flex-col items-center gap-3 text-center lg:flex-row lg:items-center lg:justify-between lg:text-left">
                    {/* Copyright + legal links */}
                    <div className="flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                        <p className="lux-body lux-body-sm whitespace-nowrap text-[#F5F5F5]/60">
                            &copy; {currentYear} LuxRally Travel. All rights reserved.
                        </p>
                        <div className="h-3 w-px bg-[#F5F5F5]/15" />
                        <MenuRenderer
                            location="footer_legal"
                            className="flex flex-wrap items-center gap-3"
                            itemClassName="lux-body lux-body-sm text-[#F5F5F5]/60 transition-colors hover:text-[#F5F5F5]/80"
                        />
                    </div>

                    {/* Seller of Travel disclaimer + link to state-by-state licensing hub.
                        Topically adjacent to the SOT registration list, and provides a
                        sitewide internal link into /travel-agent-licensing-by-state. */}
                    <div className="flex flex-col items-center gap-1 lg:items-end">
                        <Link
                            href="/travel-agent-licensing-by-state"
                            className="lux-body lux-body-sm text-[#F5F5F5]/60 transition-colors hover:text-[#F5F5F5]/80"
                        >
                            Travel agent licensing by state →
                        </Link>
                        <p className="lux-body lux-body-sm text-[#F5F5F5]/60">
                            Fla. Seller of Travel Ref. No. ST43780 &nbsp; WA Seller of Travel UBI No. 604-982-628 &nbsp; HI Seller of Travel Ref.
                            No. TAR-7477 &nbsp; CA Seller of Travel Ref. No. 2151375-70
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
